import { ScrollingModule } from '@angular/cdk/scrolling';
import { APP_BASE_HREF } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import {
  DefaultDataServiceConfig,
  EntityDataModule,
  EntityDataService,
  HttpUrlGenerator,
} from '@ngrx/data';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer, RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';

// Modules
import { ActionBarModule } from '@blaze-dashboard/action-bar-feature';
import { DataConnectorsModule } from '@blaze-dashboard/data-connectors-feature';
import { provideEditorEngineState } from '@blaze-dashboard/editor-data-access';
import { EngineModule, SubscriptionService } from '@blaze-runtime/engine-feature';
import { AppStateModule, fromRouter } from '@blaze-shared/app-state-data-access';
import {
  AdminLevelGuard,
  AuthTokenGuard,
  CurrentAccountGuard,
  ExternalUserGuard,
  ProjectRoleGuard,
  provideUserAccessRoles,
} from '@blaze-shared/auth-data-access';
import { FormulasStateModule } from '@blaze-shared/formulas-data-access';
import {
  API_BASE_URL,
  DEPLOYMENT_TARGET,
  DEV_SUBDOMAIN_OVERRIDE,
  FeatureToggleService,
  isProductionGradeDeployment,
  MEDIA_CDN_URL,
  NotificationComponent,
  SCHEMA_VALIDATION_ENABLED,
  WEBSOCKETS_URL,
} from '@blaze-shared/platform-data-access';
import { ProjectDataService } from '@blaze-shared/projects-data-access';
import { SharedModule } from '@blaze-shared/shared-feature';
import { IconsModule, ThemesService, ThemingStateModule } from '@blaze-shared/theming-data-access';
import { IThemeService } from '@blaze-shared/theming-domain';
import { UPLOADER_STATE_MODULE_CONFIG } from '@blaze-shared/uploader';

import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BlazeEffectsModule } from './bootstrap/blaze-effects.module';
import { provideEntityCaches } from './bootstrap/entity-caches.provider';
import { dataServiceConfig, entityConfig } from './data/app-data-service-config';
import { AppHttpUrlGenerator } from './data/app-http-generator';
import { AppInterceptor } from './data/app-interceptor';
import { SERVICE_WORKER_MODULE } from './data/service-worker';
import { StaticRedirectGuardService } from './static-redirect-guard.service';

const STORE_DEV_TOOLS =
  isProductionGradeDeployment(environment.deploymentTarget) && !environment.prodDebugOverride
    ? []
    : StoreDevtoolsModule.instrument({
        name: 'Blaze',
        autoPause: true,
        maxAge: 300,
        connectInZone: true,
      });

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ScrollingModule,
    AppRoutingModule,
    // why ahead of `BlazeEffectsModule`: it registers the effect that dispatches hydration as
    // soon as it loads, and a feature reducer that is not in the store yet drops what it is sent.
    AppStateModule,
    // why here: both register state the bootstrap effects' actions are reacted to, so they must
    // precede `BlazeEffectsModule`.
    FormulasStateModule,
    ThemingStateModule,
    BlazeEffectsModule,
    IconsModule,
    NotificationComponent,
    EngineModule,
    ActionBarModule,
    SharedModule,
    SERVICE_WORKER_MODULE,
    StoreModule.forRoot(
      {
        [fromRouter.ROUTER_STATE_KEY]: routerReducer,
      },
      {
        metaReducers: [],
        runtimeChecks: {
          strictActionImmutability: false,
          strictActionTypeUniqueness: true,
          strictStateImmutability: false,
          strictStateSerializability: true,
          strictActionSerializability: false,
        },
      },
    ),
    StoreRouterConnectingModule.forRoot({
      routerState: RouterState.Minimal,
    }),
    EffectsModule.forRoot([SubscriptionService]),
    UPLOADER_STATE_MODULE_CONFIG,
    EntityDataModule.forRoot(entityConfig),
    DataConnectorsModule,
    STORE_DEV_TOOLS,
  ],
  providers: [
    provideEntityCaches(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    // why here and not in `EditorModule`: `EngineModule` above builds the `engine` feature reducer
    // at bootstrap, so the editor's slices have to be registered in this, the root, injector.
    ...provideEditorEngineState(),
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    { provide: DefaultDataServiceConfig, useValue: dataServiceConfig },
    { provide: API_BASE_URL, useValue: environment.apiUrl },
    { provide: DEPLOYMENT_TARGET, useValue: environment.deploymentTarget },
    { provide: DEV_SUBDOMAIN_OVERRIDE, useValue: environment.subdomainOverride },
    { provide: MEDIA_CDN_URL, useValue: environment.mediaCdnUrl },
    { provide: SCHEMA_VALIDATION_ENABLED, useValue: environment.schemaValidation },
    { provide: WEBSOCKETS_URL, useValue: environment.websocketsUrl },
    AdminLevelGuard,
    AuthTokenGuard,
    CurrentAccountGuard,
    ExternalUserGuard,
    ProjectRoleGuard,
    StaticRedirectGuardService,
    // why here rather than in the auth library: the service it constructs has to exist before the
    // first project loads, and only the app shell is guaranteed to be up by then.
    provideUserAccessRoles(),
    FeatureToggleService,
    AppInterceptor,
    { provide: HttpUrlGenerator, useClass: AppHttpUrlGenerator },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: { disableTooltipInteractivity: true } as MatTooltipDefaultOptions,
    },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } as RippleGlobalOptions },
    { provide: IThemeService, useExisting: ThemesService },
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true, hideMultipleSelectionIndicator: true },
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  private readonly entityDataService = inject(EntityDataService);
  private readonly projectDataService = inject(ProjectDataService);

  constructor() {
    this.entityDataService.registerService('project', this.projectDataService);
  }
}
